import { consume } from '@lit/context';
import { localized } from '@weavedev/lit-i18next';
import {
  html,
  LitElement,
  nothing,
  PropertyValueMap,
  TemplateResult,
} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import {
  CancelablePromise,
  CancelError,
  ConfigurationDto,
  ConfiguratorLayout,
  ConfiguratorService,
  PriceBreakdownDto,
} from '@/api-sdk';
import { FgPriceDetails } from '@/components';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  personalisation,
  PersonalisationType,
  printExample,
  PrintExampleType,
} from '@/context';
import {
  ActionEvent,
  IntentActionEvent,
  IntentToEvent,
  MessageTypes,
} from '@/types';
import {
  getParamsObjectFromQueryString,
  optionParamsWithQuantities,
  saveConfiguration,
} from '@/utils';

import '../price-details/fg-price-details';
import './mobile/fg-configurator-mobile-summary';
import './summary/fg-configurator-actions-top-bottom';
import './summary/fg-configurator-summary';

@customElement('fg-configurator-actions')
@localized()
export class FgConfiguratorActions extends LitElement {
  fgPriceDetailsDialogRef: Ref<FgPriceDetails> = createRef();

  /**
   * Product code, and is used to fetch the price breakdown data
   *
   * @property {string} product
   */
  @property({ type: String })
  product = '-1';

  /**
   * Query string containing the configuration data, and is used to fetch te price breakdown data
   */
  @property({ type: String })
  queryString = '';

  /**
   * Object with configuration data see: {@link ConfigurationDto}, received from the API
   *
   * @property {ConfigurationDto} configuration
   */
  @property({ type: Object })
  configuration?: ConfigurationDto | null;

  /**
   * Custom quantity validation based on the quantity validation state\
   *
   * @property {boolean} quantityValidated
   */
  @property({ type: Boolean })
  quantityValidated = false;

  /**
   * Event to save the configuration, and is used to save the configuration and set the right checkout mode in CE
   *
   * @property {string} eventToSave
   */
  @property({ type: String })
  eventToSave?: string;

  /**
   * Boolean to determine if the print option is selected as printed or unprinted
   */
  @property({ type: Boolean })
  unprinted = false;

  /**
   * Promise that is used to fetch the price breakdown data
   */
  @state()
  priceBreakdownPromise: CancelablePromise<PriceBreakdownDto> | null = null;

  /**
   * Object containing the price breakdown data, and is used to render the price breakdown data & summary
   */
  @state()
  priceBreakDownData?: PriceBreakdownDto;

  /**
   * Loading state, and is used to render a loading state
   */
  @state()
  loading = false;

  /**
   * Error state, and is used to render an error messages
   */
  @state()
  error = false;

  /**
   * Action event, and is used to trigger the right checkout mode in CRE
   */
  @state()
  actionEvent?: ActionEvent;

  @state()
  draftValidationMessage: { message: string; type: MessageTypes } | undefined;

  @state()
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext: PersonalisationType[] = [];

  @consume({ context: printExample.context, subscribe: true })
  printExampleContext?: PrintExampleType;

  protected async willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): Promise<void> {
    if (_changedProperties.has('eventToSave') && this.eventToSave) {
      const event: { [key: string]: ActionEvent } = {
        'handle-add-to-cart': 'add-to-cart',
        'handle-add-to-quotation': 'add-to-quotation',
        'handle-request-print-proof': 'request-print-proof',
        'handle-update-cart': 'update-cart',
      };

      await this.saveConfiguration({ eventName: event[this.eventToSave] });
    }

    if (_changedProperties.has('queryString')) {
      this.loading = true;
      this.error = false;

      const priceBreakdownParams = getParamsObjectFromQueryString(
          this.queryString,
        ),
        optionParams = optionParamsWithQuantities(priceBreakdownParams),
        { configurationId, salable, product } = this.configuratorContext,
        { locale, vatRate, loggedIn, layout, country, debtor } =
          this.globalContext;

      if (!product || ((!loggedIn || !salable) && layout === 'wholesale')) {
        return;
      }

      if (
        this.priceBreakdownPromise &&
        !this.priceBreakdownPromise.isCancelled
      ) {
        this.priceBreakdownPromise.cancel();
        this.priceBreakDownData = undefined;
      }
      this.priceBreakdownPromise =
        ConfiguratorService.getGetConfiguratorPriceBreakdown({
          code: product,
          configurationId: configurationId || undefined,
          locale: locale,
          vatRate: vatRate,
          layout:
            layout === 'retail'
              ? ConfiguratorLayout.RETAIL
              : ConfiguratorLayout.WHOLESALE,
          country: country,
          debtor: debtor,
          ...optionParams,
        });

      try {
        this.priceBreakDownData = await this.priceBreakdownPromise;
      } catch (error) {
        if (error instanceof CancelError) {
          return;
        }
        this.error = true;
        console.error(error);
      } finally {
        this.loading = false;
      }
    }
  }

  renderSummary(): TemplateResult {
    switch (this.globalContext.layout) {
      case 'retail':
        return html`
          <fg-configurator-actions-top-bottom
            part=${this.globalContext.layout}
            .configuration=${this.configuration}
            .expectedDeliveryDate=${this.priceBreakDownData
              ?.expectedDeliveryDate}
            .unprinted=${this.unprinted}
            .error=${this.error}
            .loading=${this.loading}
            .priceBreakDownData=${this.priceBreakDownData}
            .quantityValidated=${this.quantityValidated}
            @open-price-details=${this.openPriceDetailsDialog}
            @intent-to=${(event: CustomEvent<IntentToEvent>) =>
              this.intentToSave({ eventName: event.detail.eventName })}
            @trigger-validation-events=${this.triggerValidationMessages}
          ></fg-configurator-actions-top-bottom>
        `;
      case 'wholesale':
        return html`
          <fg-configurator-mobile-summary
            part="wholesale"
            .priceBreakdown=${this.priceBreakDownData}
            .isLoading=${this.loading}
            .totalsExclVat=${this.priceBreakDownData?.totals?.excludingVat
              .formatted}
            @open-price-details=${this.openPriceDetailsDialog}
            @trigger-validation-events=${this.triggerValidationMessages}
          ></fg-configurator-mobile-summary>
          ${this.configuratorContext.salable && this.globalContext.loggedIn
            ? html`
                <fg-configurator-summary
                  part=${this.globalContext.layout}
                  .configuration=${this.configuration}
                  .error=${this.error}
                  .loading=${this.loading}
                  .priceBreakdown=${this.priceBreakDownData}
                  .quantityValidated=${this.quantityValidated}
                  .customerId=${this.globalContext.customerId}
                  @intent-to=${(event: CustomEvent<IntentToEvent>) =>
                    this.intentToSave({ eventName: event.detail.eventName })}
                  @trigger-validation-events=${this.triggerValidationMessages}
                  @show-draft-validation-message=${(
                    event: CustomEvent<{ message: string; type: MessageTypes }>,
                  ) => (this.draftValidationMessage = event.detail)}
                  @open-price-details=${this.openPriceDetailsDialog}
                ></fg-configurator-summary>
              `
            : nothing}
        `;
    }
  }

  render() {
    const { layout } = this.globalContext;
    return html`
      ${layout === 'wholesale'
        ? html`
            <fg-message-snack-bar
              ?show=${!!this.draftValidationMessage}
              part=${layout}
              .message="${this.draftValidationMessage?.message}"
              .type=${this.draftValidationMessage?.type || MessageTypes.SUCCESS}
              @on-close=${() => (this.draftValidationMessage = undefined)}
            ></fg-message-snack-bar>
          `
        : nothing}
      ${this.renderSummary()}
      <fg-price-details
        part=${layout}
        ${ref(this.fgPriceDetailsDialogRef)}
        .priceBreakdown=${this.priceBreakDownData}
      ></fg-price-details>
    `;
  }

  openPriceDetailsDialog() {
    if (!this.loading) {
      this.fgPriceDetailsDialogRef.value?.openDialog();
    }
  }

  async intentToSave({ eventName }: { eventName: IntentActionEvent }) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        composed: true,
      }),
    );
  }

  async saveConfiguration({ eventName }: { eventName: ActionEvent }) {
    if (!this.configuration) {
      return;
    }

    this.error = false;
    this.loading = true;
    try {
      await saveConfiguration({
        globalContext: this.globalContext,
        personalisationContext: this.personalisationContext,
        printExampleContext: this.printExampleContext,
        configuration: this.configuration,
        callback: configurationId => {
          this.dispatchEvent(
            new CustomEvent(eventName, {
              composed: true,
              detail: {
                configuration: configurationId,
              },
            }),
          );
        },
      });
    } catch {
      this.error = true;
      this.loading = false;
    } finally {
      this.actionEvent = undefined;
      this.dispatchEvent(
        new CustomEvent('reset-action-event', { composed: true }),
      );
    }
  }
  /**
   * @function triggerValidationMessages
   * @param event
   */
  triggerValidationMessages(event: CustomEvent<{ actionEvent: ActionEvent }>) {
    const { actionEvent } = event.detail;

    this.dispatchEvent(
      new CustomEvent('trigger-validation-messages', {
        composed: true,
        detail: {
          actionEvent: actionEvent,
        },
      }),
    );
  }
}
